import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import './WebhookImport.css';
import CustomPaper from '../common/CustomPaper';
import { send_component_save_request, send_request } from '../../utils/Request';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useWebhookImportStore, { getWebhookImportStore } from './useWebhookImportStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import SelectMethod from './component/SelectMethod';
import UploadSample from './component/UploadSample';
import SampleReceived from './component/SampleReceived';
import SetUpPlaceholders from './component/SetUpPlaceholders';
import SetEndpointUrl from './component/SetEndpointURL';
import SetToken from './component/SetToken';
import Summary from './component/Summary';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import OpenEndpointStep from './component/OpenEndpointStep';

const WebhookImportTemplate = (props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [webhookStep, setWebhookStep] = useState(0);
  const [method, setMethod] = useState(null);
  const [status, setStatus] = useState(null);
  const [customResponse, setCustomResponse] = useState(false);

  const store = useMemo(() => getWebhookImportStore(props.component_id), [props.component_id]);

  const componentName = useProjectStore(s => s.componentName);

  useEffect(() => {
    store.getState().setWebhookName(componentName);
  }, [componentName]);

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);
    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then(response => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            store.getState().setComponentData(components[0]);
            store.setState({ loaded: true });

            if (store.getState().name) {
              props.onComponentNameChanged(store.getState().name);
            }

            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(store.getState().lastModified);
            setStatus(components[0].status);
            setCustomResponse(components[0].customResponse ? components[0].customResponse : false);

            if (components[0].componentData) {
              if (!components[0].componentData.data.samplePlaceholders) {
                const storeState = store.getState();
                storeState.clearField();
                for (let i = 0; i < components[0].componentData.data.placeholders.length; i++) {
                  components[0].componentData.data.placeholders[i].checked = true;
                  storeState.addJsonData(components[0].componentData.data.placeholders[i]);
                }
              }
              if (components[0].componentData.token) {
                setHasToken(true);
              }
              setMethod(components[0].componentData.data.type);

              if (components[0].componentData.data.type === 'open_endpoint') {
                setActiveStep(7);
                setWebhookStep(7);
              } else if (
                components[0].componentData.token &&
                components[0].componentData.data.json != '' &&
                components[0].componentData.data.samplePlaceholders != []
              ) {
                setActiveStep(6);
                setWebhookStep(6);
              }
              setLoading(false);
            } else {
              let lp = [];
              lp.push({
                index: 0,
                placeholders: [],
                key: store.getState().keyAuto(),
                incomingKey: '',
              });
            }
          }
        }

        props.setChildTemplateLoaded(true);
        setLoading(false);
      })
      .catch(err => {
        throw Error(err.message);
      });
  }, []);

  const onChangeState = useCallback((newWebhookStep, newActiveStep) => {
    setWebhookStep(newWebhookStep);
    setActiveStep(newActiveStep);
  }, []);

  const getStepContent = (step, isExpanded) => {
    switch (step) {
      case 0:
        return (
          <SelectMethod
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            method={m => setMethod(m)}
          />
        );
      case 1:
        return (
          <UploadSample
            componentStore={store.getState()}
            status={status}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            project_id={props.project_id}
            component_id={props.component_id}
            method={method}
            version={props.version}
            isExp={isExpanded}
            customResponse={customResponse}
          />
        );
      case 2:
        return (
          <SampleReceived
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
          />
        );
      case 3:
        return (
          <SetUpPlaceholders
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            handleKeyChanges={err => setError(err)}
            method={method}
            stepStatus={'setPlaceholders'}
          />
        );
      case 4:
        return (
          <SetEndpointUrl
            onChangeState={(ws, as) => onChangeState(ws, as)}
            component_id={props.component_id}
            isExp={isExpanded}
          />
        );
      case 5:
        return (
          <SetToken
            onChangeState={(ws, as) => onChangeState(ws, as)}
            changeTokenStatus={s => setHasToken(s)}
            name={componentName}
            component_id={props.component_id}
            hasToken={hasToken}
          />
        );
      case 6:
        return (
          <Summary
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            changeTokenStatus={s => setHasToken(s)}
            name={componentName}
            component_id={props.component_id}
            stepStatus={'summary'}
            hasToken={hasToken}
            isExp={isExpanded}
          />
        );
      case 7:
        return (
          <OpenEndpointStep
            componentId={props.component_id}
            componentStore={store.getState()}
            onChangeState={(ws, as) => onChangeState(ws, as)}
            changeTokenStatus={s => setHasToken(s)}
            name={componentName}
            component_id={props.component_id}
            stepStatus={'summary'}
            hasToken={hasToken}
            isExp={isExpanded}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  if (!loading) {
    return (
      <Grid container xs={12} className="componentGrid">
        <CustomPaper className="componentPaperWebhook">
          <Grid
            container
            padding={'0 24px'}
            paddingTop={'24px'}
            marginTop={props.isExpanded ? '40px' : 0}
          >
            {getStepContent(webhookStep, props.isExpanded)}
          </Grid>
        </CustomPaper>
      </Grid>
    );
  } else return <IndividualComponentLoader />;
};

export default WebhookImportTemplate;
